import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface StepFooterProps {
  onBack?: () => void;
  onContinue?: () => void;
  onSkipToReview?: () => void;
  continueDisabled?: boolean;
  continueLabel?: string;
  continueLoading?: boolean;
  submitButton?: React.ReactNode;
}

export function StepFooter({
  onBack,
  onContinue,
  onSkipToReview,
  continueDisabled = false,
  continueLabel,
  continueLoading = false,
  submitButton,
}: StepFooterProps) {
  const { t } = useTranslation("search");
  const resolvedContinueLabel = continueLabel ?? t("wizard.footer.continue");

  return (
    <div className="flex justify-between items-center mt-6 pt-2">
      {/* Left: Back */}
      <div className="flex-1 flex justify-start">
        {onBack ? (
          <Button
            type="button"
            variant="ghost"
            onClick={onBack}
            className="gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            {t("wizard.footer.back")}
          </Button>
        ) : (
          <span />
        )}
      </div>

      {/* Center: Skip to review */}
      <div className="flex-1 flex justify-center">
        {onSkipToReview && (
          <button
            type="button"
            onClick={onSkipToReview}
            className="text-xs text-muted-foreground underline cursor-pointer hover:text-foreground transition-colors"
          >
            {t("wizard.footer.skipToReview")}
          </button>
        )}
      </div>

      {/* Right: Continue or custom submit */}
      <div className="flex-1 flex justify-end">
        {submitButton ??
          (onContinue ? (
            <Button
              type="button"
              variant="default"
              onClick={onContinue}
              disabled={continueDisabled || continueLoading}
              className="gap-1.5"
            >
              {continueLoading && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {resolvedContinueLabel}
              {!continueLoading && (
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              )}
            </Button>
          ) : null)}
      </div>
    </div>
  );
}
